import React, { useEffect, useState } from "react";
import {
  deleteCompany,
  getCompanies,
  updateCompany,
} from "../../api/add-company";
import "./CompanyList.css";

interface Company {
  _id: string;
  companyName: string;
  isActive: boolean;
  duration: string;
  phoneNumber: string;
  email: string;
  address: string;
}

const CompanyList: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editCompanyData, setEditCompanyData] = useState<Company | null>(null);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await getCompanies();
        setCompanies(response);
      } catch (err) {
        console.error("Error fetching companies:", err);
        setError("Failed to fetch companies. Please try again.");
      }
    };
    fetchCompanies();
  }, []);

  const handleDelete = async (id: string) => {
    try {
      await deleteCompany(id);
      setCompanies((companies) =>
        companies.filter((company) => company._id !== id)
      );
    } catch (err) {
      console.error("Error deleting company:", err);
      setError("Failed to delete the company. Please try again.");
    }
  };

  const handleEdit = (company: Company) => {
    setEditCompanyData(company);
    setShowEditModal(true);
  };

  const handleEditChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    setEditCompanyData((prev) =>
      prev
        ? {
            ...prev,
            [name]:
              type === "checkbox"
                ? (e.target as HTMLInputElement).checked
                : value,
          }
        : null
    );
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editCompanyData) {
      try {
        await updateCompany(editCompanyData._id, editCompanyData);
        setCompanies((prev) =>
          prev.map((company) =>
            company._id === editCompanyData._id ? editCompanyData : company
          )
        );
        setShowEditModal(false);
      } catch (err) {
        console.error("Error updating company:", err);
        setError("Failed to update the company. Please try again.");
      }
    }
  };

  return (
    <div className="company-list-container">
      <h2>Company List</h2>
      {error && (
        <div className="error-message" role="alert" aria-live="assertive">
          {error}
        </div>
      )}
      <table className="company-table" aria-label="Company List Table">
        <thead>
          <tr>
            <th>Company Name</th>
            <th>Active</th>
            <th>Duration</th>
            <th>Phone</th>
            <th>Email</th>
            <th>Address</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {companies?.map((company) => (
            <tr key={company._id}>
              <td>{company.companyName}</td>
              <td>{company.isActive ? "Yes" : "No"}</td>
              <td>{company.duration}</td>
              <td>
                <a href={`tel:${company.phoneNumber}`} className="company-link">
                  {company.phoneNumber}
                </a>
              </td>
              <td>
                <a href={`mailto:${company.email}`} className="company-link">
                  {company.email}
                </a>
              </td>
              <td>{company.address}</td>
              <td>
                <button
                  onClick={() => handleEdit(company)}
                  className="edit-button"
                  aria-label={`Edit ${company.companyName}`}>
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(company._id)}
                  className="delete-button"
                  aria-label={`Delete ${company.companyName}`}>
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Edit Modal */}
      {showEditModal && editCompanyData && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal">
            <h2>Edit Company</h2>
            <form onSubmit={handleEditSubmit}>
              <input
                type="text"
                name="companyName"
                placeholder="Company Name"
                value={editCompanyData.companyName}
                onChange={handleEditChange}
                required
                aria-required="true"
              />
              <input
                type="text"
                name="duration"
                placeholder="Duration"
                value={editCompanyData.duration}
                onChange={handleEditChange}
                required
                aria-required="true"
              />
              <input
                type="text"
                name="phoneNumber"
                placeholder="Phone Number"
                value={editCompanyData.phoneNumber}
                onChange={handleEditChange}
                required
                aria-required="true"
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={editCompanyData.email}
                onChange={handleEditChange}
                required
                aria-required="true"
              />
              <input
                type="text"
                name="address"
                placeholder="Address"
                value={editCompanyData.address}
                onChange={handleEditChange}
                required
                aria-required="true"
              />
              <div className="switch-container">
                <label htmlFor="isActive">Activate:</label>
                <input
                  type="checkbox"
                  name="isActive"
                  id="isActive"
                  checked={editCompanyData.isActive}
                  onChange={handleEditChange}
                  aria-checked={editCompanyData.isActive}
                />
              </div>
              <button type="submit" className="submit-button">
                Save
              </button>
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                className="close-button">
                Cancel
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyList;
